import type { ElementKind } from './types'

/**
 * Making text fit the paper.
 *
 * internal/layout/fit.go ported, line for line and decision for decision. The Go
 * side is what the printer runs; this is what the editor previews with, and the
 * whole value of a preview is that it is not a second opinion. A designer who is
 * shown a name that fits and hands the customer a slip where it does not has been
 * lied to by the one tool that existed to stop that happening.
 *
 * The rule, both sides:
 *
 * - one row shrinks until the words fit and stops at six points, below which a
 *   thermal head prints grey rather than letters; what still does not fit is cut;
 * - two or three rows wrap at the size the design asked for and cut what is left,
 *   breaking a word too long for the line rather than hyphenating it;
 * - no limit changes nothing at all, which is what every design drawn before this
 *   existed means.
 *
 * What is not shared is the measuring. The printer sets DejaVu against 203-dot
 * paper and a browser sets the design system's own face against CSS pixels, so the
 * two will not always break a line at the same word. What they always agree on is
 * what happened to the text — shrunk, and how far it was allowed to go; how many
 * rows it got; and that the rest was cut. That is what a designer needs to see.
 *
 * fit.test.ts and internal/layout/fit_test.go hold the same cases in the same
 * order against the same made-up typeface. A case added to one belongs in the
 * other on the same day.
 */

/** How the caller's own typesetting answers the two questions the rule asks. */
export interface Measure {
  /** How wide a string is at a size, in the canvas's units. */
  width(text: string, size: number): number
  /** How tall one line is at that size. */
  height(size: number): number
  /** The smallest size a single row may shrink to, in those same units. Omitted
   *  means MIN_FIT_SIZE. Stated by the caller because the printer's copy of this
   *  rule works in printer dots and this one works in canvas units, and the two
   *  are equal only on 80mm paper — see MIN_FIT_SIZE. */
  floor?: number
}

/** How a string is to be set, and what getting there cost. */
export interface Fitted {
  size: number
  lines: string[]
  shrunk: boolean
  cropped: boolean
}

/**
 * The most rows a ticket's text may be given. Three is Qmatic's limit and is also
 * the honest one: a fourth row on a slip that is mostly a number is a paragraph
 * nobody standing up reads.
 */
export const MAX_TICKET_ROWS = 3

/**
 * The floor a single row shrinks to, in printer dots — six points at 203 dots per
 * inch, rounded up from 16.9. Below it a thermal head prints grey rather than
 * letters.
 *
 * A ticket canvas unit is one printer dot **on 80mm paper**, which is what this
 * preview assumes. On 58mm the agent scales the whole design to two thirds, so the
 * head reaches six points while the canvas still has room: the slip cuts a name
 * this preview shows whole. The editor cannot do better on its own, because one
 * design serves machines with different paper and the document does not say which
 * — so paperCannotDo warns about 58mm instead, and this is half of what it is
 * warning about.
 */
export const MIN_FIT_SIZE = 17

/** How far one attempt steps down from the last. A ratio, and not rounded: rounding
 *  is where two implementations of one rule start disagreeing about which attempt
 *  was the one that fitted. */
const FIT_STEP = 0.92

/** One character rather than three dots, on a line already short of room. */
const ELLIPSIS = '…'

/**
 * Whether a row limit means anything for an element kind — the mirror of
 * layout.FitsRows.
 *
 * Text and the branch name, and deliberately not the rest: these are the two whose
 * length the design cannot predict. The ticket number sets itself as large as its
 * box allows and is already fitted, the date is a fixed number of characters, and
 * the position and wait are sentences this product writes rather than ones a
 * customer can lengthen.
 */
export function fitsRows(kind: ElementKind): boolean {
  return kind === 'text' || kind === 'branchName'
}

/**
 * Decide how a string is set inside a box when the design has limited it to `rows`
 * lines. `start` is the size the element would have taken with nothing in its way.
 */
export function fitText(
  text: string,
  rows: number,
  width: number,
  height: number,
  start: number,
  m: Measure,
): Fitted {
  const trimmed = text.trim()
  if (trimmed === '' || start <= 0 || width <= 0 || height <= 0) {
    return { size: start, lines: [], shrunk: false, cropped: false }
  }
  // No limit asked for. The caller's own behaviour stands; this rule does not
  // invent one for it.
  if (rows < 1) {
    return { size: start, lines: [trimmed], shrunk: false, cropped: false }
  }
  const limit = Math.min(rows, MAX_TICKET_ROWS)

  if (limit === 1) {
    let size = start
    const floor = m.floor && m.floor > 0 ? m.floor : MIN_FIT_SIZE
    while (size > floor && !fitsOneLine(trimmed, size, width, height, m)) {
      size *= FIT_STEP
      if (size < floor) size = floor
    }
    const shrunk = size < start
    if (fitsOneLine(trimmed, size, width, height, m)) {
      return { size, lines: [trimmed], shrunk, cropped: false }
    }
    // At the floor and still too long: cut rather than go on shrinking, and cut
    // rather than overrun — a name that runs into the element below it takes that
    // one with it.
    return { size, lines: [ellipsise(trimmed, width, size, m)], shrunk, cropped: true }
  }

  // Two rows or three: the size the design asked for, and what does not fit is cut.
  // No shrinking, which is Qmatic's rule and is the right way round — a designer
  // who asked for three rows has already decided how much room the words get.
  const size = start
  let room = limit
  const lineHeight = m.height(size)
  if (lineHeight > 0) {
    const byHeight = Math.floor(height / lineHeight)
    if (byHeight < room) room = byHeight
  }
  // A box too short for even one line still gets one. It will overhang, and an
  // overhanging line is visible; no line at all is an element that silently
  // vanished from the paper.
  if (room < 1) room = 1

  const { lines, cropped } = wrapTo(trimmed, room, width, size, m)
  return { size, lines, shrunk: false, cropped }
}

/** Whether the whole string sits on one line inside the box, both ways. Height is
 *  asked as well as width because a designer's own scale goes up to four times, and
 *  a line set four times too large leaves the top of its box before the side. */
function fitsOneLine(text: string, size: number, width: number, height: number, m: Measure): boolean {
  return m.width(text, size) <= width && m.height(size) <= height
}

/**
 * Break text to the width, by word, never producing more than `rows` lines, and say
 * whether anything was left over.
 *
 * A word too long for the line on its own is broken where it stops fitting, not
 * hyphenated: a hyphen this rule inserted would be a hyphen the customer reads back
 * as part of their own name or reference number.
 */
function wrapTo(
  text: string,
  rows: number,
  width: number,
  size: number,
  m: Measure,
): { lines: string[]; cropped: boolean } {
  const queue = text.split(/\s+/).filter((w) => w !== '')
  const lines: string[] = []
  let current = ''

  while (queue.length > 0 && lines.length < rows) {
    const word = queue[0] as string
    const candidate = current === '' ? word : `${current} ${word}`
    if (m.width(candidate, size) <= width) {
      current = candidate
      queue.shift()
      continue
    }
    if (current !== '') {
      // The line is full. The word starts the next one.
      lines.push(current)
      current = ''
      continue
    }
    const [head, rest] = breakWord(word, width, size, m)
    lines.push(head)
    if (rest === '') {
      queue.shift()
      continue
    }
    queue[0] = rest
  }
  if (current !== '' && lines.length < rows) {
    lines.push(current)
    current = ''
  }

  const cropped = queue.length > 0 || current !== ''
  if (cropped && lines.length > 0) {
    lines[lines.length - 1] = ellipsise(lines[lines.length - 1] as string, width, size, m)
  }
  return { lines, cropped }
}

/** Split one word at the last character that still fits. Never an empty head: a
 *  width too narrow for a single character would otherwise never terminate. */
function breakWord(word: string, width: number, size: number, m: Measure): [string, string] {
  const runes = Array.from(word)
  let cut = 0
  for (let i = 1; i <= runes.length; i++) {
    if (m.width(runes.slice(0, i).join(''), size) > width) break
    cut = i
  }
  if (cut === 0) cut = 1
  return [runes.slice(0, cut).join(''), runes.slice(cut).join('')]
}

/**
 * Mark a line as cut, giving up as many characters as the mark needs.
 *
 * The mark is what makes a cropped ticket readable as a cropped ticket. Without it
 * a customer holding a slip that says "Məmmədova Leyla Rəşid" has no way to know
 * that is not their whole name, and neither has the clerk reading it back.
 */
function ellipsise(line: string, width: number, size: number, m: Measure): string {
  let runes = Array.from(line)
  while (runes.length > 0) {
    const candidate = runes.join('') + ELLIPSIS
    if (m.width(candidate, size) <= width) return candidate
    runes = runes.slice(0, -1)
  }
  return ELLIPSIS
}

/** How far a text may shrink when the design does not say (layout.DefaultMinScale). */
export const DEFAULT_MIN_SCALE = 0.6
/** The smallest share a design may allow (layout.LowestMinScale). */
export const LOWEST_MIN_SCALE = 0.3

/** An element's shrink floor as a share of its set size (Element.MinScaleOf). */
export function minScaleOf(minScale: number | undefined): number {
  if (!minScale || minScale <= 0) return DEFAULT_MIN_SCALE
  return Math.min(minScale, 1)
}

/**
 * Auto-fit, internal/layout/autofit.go ported: a text with no row limit shrinks in
 * the rule's steps until every row fits, down to start×minScale (never below
 * m.floor); words are not broken while shrinking helps. At the floor the rows that
 * fit are kept and the last is cut, and `cropped` says the text did not fit.
 */
export function autoFit(text: string, width: number, height: number, start: number, minScale: number, m: Measure): Fitted {
  const trimmed = text.trim()
  if (trimmed === '' || start <= 0 || width <= 0 || height <= 0) {
    return { size: start, lines: [], shrunk: false, cropped: false }
  }
  let floor = start * Math.min(minScale > 0 ? minScale : DEFAULT_MIN_SCALE, 1)
  if (m.floor && m.floor > floor) floor = m.floor
  floor = Math.min(floor, start)

  let size = start
  for (;;) {
    const whole = setWhole(trimmed, width, height, size, m)
    if (whole) return { size, lines: whole, shrunk: size < start, cropped: false }
    if (size <= floor) break
    size *= FIT_STEP
    if (size < floor) size = floor
  }
  const lh = m.height(size)
  const room = lh > 0 ? Math.max(Math.floor(height / lh), 1) : 1
  return { size, lines: wrapParagraphs(trimmed, room, width, size, m).lines, shrunk: size < start, cropped: true }
}

function setWhole(text: string, width: number, height: number, size: number, m: Measure): string[] | null {
  const words = text.split(/\s+/).filter((w) => w !== '')
  if (words.some((w) => m.width(w, size) > width)) return null
  const maxRows = words.length + (text.match(/\n/g)?.length ?? 0) + 1
  const { lines, cropped } = wrapParagraphs(text, maxRows, width, size, m)
  if (cropped || lines.length * m.height(size) > height) return null
  return lines
}

function wrapParagraphs(text: string, rows: number, width: number, size: number, m: Measure): { lines: string[]; cropped: boolean } {
  const out: string[] = []
  for (const p of text.replace(/\r\n/g, '\n').split('\n')) {
    const left = rows - out.length
    if (left <= 0) {
      if (out.length > 0) out[out.length - 1] = ellipsise(out[out.length - 1] as string, width, size, m)
      return { lines: out, cropped: true }
    }
    if (p.trim() === '') {
      out.push('')
      continue
    }
    const { lines, cropped } = wrapTo(p, left, width, size, m)
    out.push(...lines)
    if (cropped) return { lines: out, cropped: true }
  }
  return { lines: out, cropped: false }
}
